/* eslint-disable no-loop-func */
import { isRootUser } from '@orientaction/utils';
import {
  CONSULTANT_ROLE_ID,
  DIRECTION_ROLE_ID,
  MANAGER_2_ROLE_ID,
  MANAGER_3_ROLE_ID,
} from '../shared/constants';
import { buildServiceResourcesMapFromAssignments, ServiceResourceIds } from '../shared/serviceResourcesMap';
import { toId } from '../shared/utils';

export type AssignmentResourceFilter = {
  majorTestIds: Set<number>;
  minorTestIds: Set<number>;
  bookIds: Set<number>;
  ebookIds: Set<number>;
  gameIds: Set<number>;
};

const TEAM_ROLE_IDS = new Set([
  DIRECTION_ROLE_ID,
  MANAGER_3_ROLE_ID,
  MANAGER_2_ROLE_ID,
  CONSULTANT_ROLE_ID,
]);

const readOwnerId = (value: any): number | null => {
  if (value == null) {
    return null;
  }
  if (typeof value === 'object') {
    return toId(value.id ?? value.data?.id);
  }
  return toId(value);
};

const readViewer = (raw: any) => {
  const id = toId(raw?.id);
  const roleId = toId(raw?.company_user_role?.id ?? raw?.company_user_role);
  const companyId = toId(raw?.company?.id ?? raw?.company?.data?.id ?? raw?.company);
  return { id, roleId, companyId, isRoot: isRootUser(raw) };
};

export const shouldSkipAssignmentFilter = (viewer: any) => {
  const current = readViewer(viewer);
  return current.isRoot || !current.id || !current.roleId || !TEAM_ROLE_IDS.has(current.roleId);
};

export const needsTeamOwnerIds = (viewer: any) => {
  const current = readViewer(viewer);
  return (
    !current.isRoot &&
    (current.roleId === DIRECTION_ROLE_ID ||
      current.roleId === MANAGER_3_ROLE_ID ||
      current.roleId === MANAGER_2_ROLE_ID)
  );
};

export const collectHierarchyUserIds = (payload: any): number[] => {
  const ids = new Set<number>();

  const visit = (node: any, depth = 0) => {
    if (!node || depth > 6) {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(item => visit(item, depth + 1));
      return;
    }
    if (typeof node !== 'object') {
      return;
    }
    const id = toId(node.id ?? node.attributes?.id);
    const looksLikeUser = Boolean(
      node.firstname != null ||
        node.lastname != null ||
        node.username != null ||
        node.company_user_role ||
        node.attributes?.firstname ||
        node.attributes?.lastname
    );
    if (id && looksLikeUser) {
      ids.add(id);
    }
    Object.values(node).forEach(value => {
      if (value && typeof value === 'object') {
        visit(value, depth + 1);
      }
    });
  };

  visit(payload);
  return Array.from(ids);
};

export const collectDescendantIds = (viewerId: number, staff: any[]): number[] => {
  const parentById = new Map<number, number>();
  staff.forEach(item => {
    const id = toId(item?.id ?? item);
    const parentId = readOwnerId(item?.upper_hierarchy ?? item?.attributes?.upper_hierarchy);
    if (id && parentId) {
      parentById.set(id, parentId);
    }
  });

  const ids = new Set<number>([viewerId]);
  let changed = true;
  while (changed) {
    changed = false;
    parentById.forEach((parentId, id) => {
      if (!ids.has(id) && ids.has(parentId)) {
        ids.add(id);
        changed = true;
      }
    });
  }
  return Array.from(ids);
};

export const buildAssignmentResourceFilter = (
  beneficiary: any,
  ownerIds: number[]
): AssignmentResourceFilter => {
  const owners = new Set(ownerIds.filter(id => id > 0));
  const fallbackOwner = readOwnerId(beneficiary?.upper_hierarchy);
  const fromAssignments = buildServiceResourcesMapFromAssignments(beneficiary?.service_assignments);
  const fromMap = beneficiary?.service_resources_map || {};
  const keys = new Set([...Object.keys(fromAssignments), ...Object.keys(fromMap)]);

  const majorTestIds = new Set<number>();
  const minorTestIds = new Set<number>();
  const bookIds = new Set<number>();
  const ebookIds = new Set<number>();
  const gameIds = new Set<number>();

  const addIds = (target: Set<number>, values?: number[]) => {
    (values || []).forEach(id => {
      if (id > 0) {
        target.add(id);
      }
    });
  };

  keys.forEach(key => {
    const assignmentEntry = fromAssignments[key] || {};
    const mapEntry = fromMap[key] || {};
    const entry: ServiceResourceIds = {
      ...mapEntry,
      ...assignmentEntry,
      upper_hierarchy: assignmentEntry.upper_hierarchy ?? mapEntry.upper_hierarchy,
    };
    const ownerId = readOwnerId(entry.upper_hierarchy) || fallbackOwner;
    if (!ownerId || !owners.has(ownerId)) {
      return;
    }
    addIds(majorTestIds, entry.major_test_ids);
    addIds(minorTestIds, entry.tests_to_do);
    addIds(bookIds, entry.book_template_ids);
    addIds(ebookIds, entry.authorized_ebooks);
    addIds(gameIds, entry.authorized_games);
  });

  return { majorTestIds, minorTestIds, bookIds, ebookIds, gameIds };
};

export const isAllowedId = (ids: Set<number> | undefined, value: any) => {
  if (!ids) {
    return true;
  }
  const id = toId(value);
  return Boolean(id && ids.has(id));
};
